import { todayPT } from "@homebound/activesupport";
import type { TagProps } from "@homebound/beam";
import type { NamedFragment } from "src/fragments/commonFragments.generated";
import type { Maybe } from "src/generated/graphqlTypes";
import type { ProductOptionCard_CheckoutConfigPotentialOptionFragment } from "src/routes/projects/change-events/guided/workflows/addOrModifyOptions/components/ProductOptionCard.generated";
import { formatList, pluralize } from "src/utils";
import type { Temporal } from "temporal-polyfill";

/** Only warn about cutoffs within the next 3 weeks, same as checkout does. */
const cutoffWarningDays = 21;

/** A `Tag` for an option group's cutoff, once it's near enough to matter; `undefined` otherwise. */
export function cutoffTag(cutoffDate: Maybe<Temporal.PlainDate>): TagProps<any> | undefined {
  if (!cutoffDate) return undefined;
  const days = todayPT().until(cutoffDate, { largestUnit: "day" }).days;
  if (days < 0) return { text: "Expired", type: "error" };
  if (days === 0) return { text: "Expires today", type: "warning" };
  if (days > cutoffWarningDays) return undefined;
  return { text: `${days} ${pluralize(days, "day")} to cutoff`, type: "warning" };
}

/**
 * Checkout's "Option Conflict" message for an option whose toggle would drop other selected options (the checkoutConfig's
 * `wouldDeselect`), i.e. "Selecting this 'H2 - English Cottage' will deselect the 'EXTSCH005 - Coastal Villa' option
 * due to conflicts or dependencies."; `undefined` when nothing would be dropped.
 */
export function conflictMessage(
  optionName: string,
  wouldDeselect: NamedFragment[],
  action: "Selecting" | "Removing" = "Selecting",
): string | undefined {
  if (wouldDeselect.isEmpty) return undefined;
  const names = formatList(wouldDeselect.map((o) => `'${o.name}'`));
  return `${action} this '${optionName}' will deselect the ${names} ${pluralize(wouldDeselect.length, "option")} due to conflicts or dependencies.`;
}

/** The option's own image, else its primary product's (package groups shown as a single product have no `optionImage`). */
export function imageUrl(potential: ProductOptionCard_CheckoutConfigPotentialOptionFragment): string | undefined {
  return (
    potential.option.optionImage?.previewUrl ?? potential.primaryProduct?.featuredImage?.asset?.previewUrl ?? undefined
  );
}
